import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, Grid, Stack } from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';

const defaultValues = {
    name: '',
    electricity_price: '',
    gas_price: '',
    oil_price: '',
    T_inside_heating: '',
    daily_hours: '',
    nightly_hours: '',
    lat: '',
    lon: '',
    budget: '',
    available_area: ''
};

const BuildingForm = ({ initialValues, onSubmit, onCancel, submitLabel = 'Submit', isEdit = false }) => {
    const [formData, setFormData] = useState(defaultValues);

    useEffect(() => {
        if (initialValues) setFormData(initialValues);
    }, [initialValues]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Prepare payload with correct types and validation
        const payload = {
            name: formData.name,
            electricity_price: parseFloat(Number(formData.electricity_price).toFixed(2)),
            gas_price: parseFloat(Number(formData.gas_price).toFixed(2)),
            oil_price: parseFloat(Number(formData.oil_price).toFixed(2)),
            T_inside_heating: parseInt(formData.T_inside_heating),
            daily_hours: parseInt(formData.daily_hours),
            nightly_hours: parseInt(formData.nightly_hours),
            lat: parseFloat(formData.lat),
            lon: parseFloat(formData.lon),
            budget: parseFloat(Number(formData.budget).toFixed(2)),
            available_area: parseFloat(Number(formData.available_area).toFixed(2))
        };
        onSubmit(payload);
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, width: { xs: '100%', sm: 500, md: 700, lg: 900, xl: 1100 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                <ApartmentIcon sx={{ fontSize: 60, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" align="center" fontWeight="bold" gutterBottom>
                    {isEdit ? 'Edit Building' : 'Add Building'}
                </Typography>
            </Box>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        name="name"
                        label="Name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        name="electricity_price"
                        label="Electricity Price (€ / kWh)"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        value={formData.electricity_price}
                        onChange={handleChange}
                        required
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        name="gas_price"
                        label="Gas Price (€ / m³)"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        value={formData.gas_price}
                        onChange={handleChange}
                        required
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        name="oil_price"
                        label="Oil Price (€ / lt)"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        value={formData.oil_price}
                        onChange={handleChange}
                        required
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        name="T_inside_heating"
                        label="Desired Indoor Temperature (°C)"
                        type="number"
                        inputProps={{ min: 0, max: 40, step: 1 }}
                        value={formData.T_inside_heating}
                        onChange={handleChange}
                        required
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        name="daily_hours"
                        label="Daily Active Hours"
                        type="number"
                        inputProps={{ min: 0, max: 24, step: 1 }}
                        value={formData.daily_hours}
                        onChange={handleChange}
                        required
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        name="nightly_hours"
                        label="Nightly Active Hours"
                        type="number"
                        inputProps={{ min: 0, max: 12, step: 1 }}
                        value={formData.nightly_hours}
                        onChange={handleChange}
                        required
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        name="lat"
                        label="Latitude"
                        type="number"
                        inputProps={{ step: 0.000001 }}
                        value={formData.lat}
                        onChange={handleChange}
                        required
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        name="lon"
                        label="Longitude"
                        type="number"
                        inputProps={{ step: 0.000001 }}
                        value={formData.lon}
                        onChange={handleChange}
                        required
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        name="budget"
                        label="Available Budget (€)"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        value={formData.budget}
                        onChange={handleChange}
                        required
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        name="available_area"
                        label="Available Area for PV (m²)"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        value={formData.available_area}
                        onChange={handleChange}
                        required
                        fullWidth
                    />
                </Grid>
            </Grid>
            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                {onCancel && <Button variant="outlined" onClick={onCancel} color="secondary">Cancel</Button>}
                <Button type="submit" variant="contained">{submitLabel}</Button>
            </Stack>
        </Box>
    );
};

export default BuildingForm; 